import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

// Components
import { Box, Center, Text } from '@wegroup/design-system';
import AssistantQuestion from '../components/flow/AssistantQuestion';

// Services
import { useTranslation } from 'react-i18next';
import IllustrationIconSelector from '../../../common/src/components/IllustrationIconSelector';
import { createLouiseOffer } from '../services/offersService';
import { LouiseQuote } from '../../../../types/Quotes';
import { LouiseSessionObject } from '../../../../types/ProductFactory';
import { useLocale } from '../../../../i18n';
import { isErrorResponse } from '../../../../types/API';
import { setOfferIdForQuote } from '../store/redux/slices/offersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { loadSelectionState } from '../store/redux/slices/selectionSlice';
import { useQueryClient } from 'react-query';
import { RootState } from '../store/redux/reducers/rootReducer';
import { useFlowSessionObject } from '../hooks/useFlowQueries';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import CampaignsSignedOfferBottomContainer from '../components/signed-offers/CampaignsSignedOfferBottomContainer';
import FlowerContainer from '../components/intro/FlowerContainer';
import useAuth from '../../../app/src/features/authentication/hooks/useAuth';
import LoadingSpinner from '../../../common/src/components/LoadingSpinner';

const CampaignsOfferFinalizationView: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();
  const { user, distribution } = useAuth();
  const { userLang } = useLocale();
  const sessionId = useSessionId();

  const generatedOffersForQuotes = useSelector(
    (state: RootState) => state.offers.generatedOffersForQuotes,
  );
  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const quoteSpecs = useSelector((state: RootState) => state.offers.quoteSpecs);
  const selectedRiskObjects = useSelector(
    (state: RootState) => state.selection.selectedRiskObjects,
  );

  const queryClient = useQueryClient();

  const { data: sessionState } = useFlowSessionObject(sessionId);

  useEffect(() => {
    initial();
  }, []);

  useEffect(() => {
    if (sessionState) {
      selectedRiskObjects.forEach(async (riskObject) => {
        await Promise.all(
          selectedOffers[riskObject]?.map((quoteId) => {
            if (generatedOffersForQuotes[quoteId] != null)
              return Promise.resolve(null);
            const quote = queryClient.getQueryData<LouiseQuote | undefined>([
              sessionId,
              'quotes',
              riskObject,
              quoteId,
              quoteSpecs[quoteId],
            ]);

            if (quote) return createOfferAsync(quote, sessionState);

            return Promise.resolve(null);
          }),
        );
        setIsLoading(false);
      });
    }
  }, [sessionState]);

  const initial = () => {
    // Fixes an issue where page refresh would cause all session data to be lost
    dispatch(loadSelectionState(sessionId));
  };

  const createOfferAsync = async (
    quote: LouiseQuote,
    sessionState: LouiseSessionObject,
  ) => {
    const [resp] = await createLouiseOffer(
      quote,
      sessionState,
      distribution,
      userLang,
      user,
    );

    if (!isErrorResponse(resp)) {
      dispatch(
        setOfferIdForQuote({ quoteId: quote.hash_id, offerId: resp?.offer_id }),
      );
    }

    return resp;
  };

  return (
    <Center
      minH="100%"
      w="100%"
      overflowX="hidden"
      overflowY="auto"
      backgroundColor="secondary.800"
    >
      <StyledFlowerContainer maxWidth="118.2rem">
        {isLoading ? (
          <Center flexDir="column" h="400px">
            <Box>
              <LoadingSpinner />
              <Text
                mt="4"
                textStyle={{ base: 'h6Headline', laptop: 'h4Headline' }}
              >
                {t('Sending offers to the broker')}
              </Text>
            </Box>
          </Center>
        ) : (
          <>
            <StyledAssistantQuestion
              question={t(
                'The broker received your offers You can now safely close this page',
              )}
            />
            <Box maxW="90vw" height="30vh" mb="8">
              <StyledIllustrationIconSelector type="partyhat" />
            </Box>
            <CampaignsSignedOfferBottomContainer />
          </>
        )}
      </StyledFlowerContainer>
    </Center>
  );
};

const StyledIllustrationIconSelector = styled(IllustrationIconSelector)`
  width: 100%;
  height: 100%;
`;

const StyledFlowerContainer = styled(FlowerContainer)`
  > div:last-child {
    border-radius: 10px;
  }
`;

const StyledAssistantQuestion = styled(AssistantQuestion)`
  margin: 0;
  padding: 1.6rem;
  align-self: center;
`;

export default CampaignsOfferFinalizationView;
